/**
 * Chat System Query Hooks
 * React Query hooks for AI chatbot interactions
 */

import { useQuery } from '@tanstack/react-query';
import {
  listSessions,
  getSession,
  getChatStats,
} from '../services/chatApi';
import type { ListSessionsRequest } from '../types/help.types';

// Query keys for caching
export const chatKeys = {
  all: ['chat'] as const,
  sessions: () => [...chatKeys.all, 'sessions'] as const,
  sessionList: (params?: ListSessionsRequest) => [...chatKeys.sessions(), 'list', params] as const,
  sessionDetail: (sessionId: string) => [...chatKeys.sessions(), 'detail', sessionId] as const,
  stats: () => [...chatKeys.all, 'stats'] as const,
};

/**
 * Hook to fetch chat sessions list
 */
export const useChatSessions = (params?: ListSessionsRequest) => {
  return useQuery({
    queryKey: chatKeys.sessionList(params),
    queryFn: () => listSessions(params),
    staleTime: 30 * 1000, // 30 seconds - sessions are dynamic
  });
};

/**
 * Hook to fetch a specific chat session with full message history
 */
export const useChatSession = (sessionId: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: chatKeys.sessionDetail(sessionId),
    queryFn: () => getSession(sessionId),
    enabled: options?.enabled ?? !!sessionId,
    staleTime: 10 * 1000, // 10 seconds - conversation history is dynamic
  });
};

/**
 * Hook to fetch chat statistics (usage, limits, etc.)
 */
export const useChatStats = () => {
  return useQuery({
    queryKey: chatKeys.stats(),
    queryFn: getChatStats,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Auto-refetch every minute to keep stats fresh
  });
};

// Default export
export default useChatSessions;
